import React, { useCallback, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useCreateWithAiItem } from '@jira/ai-first-create__app/src/useCreateWithAiItem.tsx';
import { useCreateWithPlannerItem } from '@jira/ai-first-create__app/src/useCreateWithPlannerItem.tsx';
import {
	AiPlannerIframeProvider,
	useAiPlannerIframe,
} from '@jira/ai-planning__ai-planner-iframe-modal/src/AiPlannerIframeProvider.tsx';
import { useAiPlannerUniversalCreateEnabled } from '@jira/ai-planning__ai-plans-overview/src/useAiPlannerUniversalCreateEnabled.tsx';
import { useAiFirstCreateEnabled } from '@jira/router-resources__ai-first-create/src/useAiFirstCreateEnabled.tsx';
import { NavigationCreateIssueButtonWrapperNudgeAsync } from '@jira/software__onboarding-nudges/src/NavigationCreateIssueButtonWrapperNudgeAsync.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import Placeholder from '@atlassian/jira-placeholder';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import type { UniversalCreateMenuSection } from '@atlassian/universal-create/types';
import { UniversalCreateButton } from '@atlassian/universal-create';

import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

import { TopLevelErrorBoundary } from '@jira/navigation-apps__topnav-common/src/ui/error-boundary/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
import { LazyLoadedSmartCreationModal } from '@atlassian/jira-smart-creation';

import {
	GLOBAL_CREATE_BUTTON_NAV4,
	GLOBAL_CREATE_BUTTON_WRAPPER,
	UNIVERSAL_CREATE_BUTTON_ID,
} from './constants.tsx';
import { useCreateIssueItem } from './hooks/use-universal-create-items.tsx';
import { useUniversalCreateAnalytics } from './hooks/use-analytics-tracker.tsx';
import {
	useUniversalCreateLoadExperience,
	useUniversalCreateButtonInteractiveExperienceStart,
	useUniversalCreateButtonInteractiveExperienceComplete,
} from './hooks/use-universal-create-ufo.tsx';
import { useShouldShowDefaultCreateButton } from './hooks/useShouldShowDefaultCreateButton.tsx';
import { preloadIssueCreateModernisedModal } from '@jira/issue-create__issue-create-modernised/src/modal/AsyncIssueCreateModernisedModal.tsx';
import { preloadModalLoadQuery } from '@jira/issue-create__issue-create-modal/src/services/modal-load-query/preloadModalLoadQuery.tsx';
import { useStorageVariables } from '@jira/issue-create__issue-create-modal/src/ui/modal/utils.tsx';
import { useSmartFieldsMachineLearning } from '@jira/issue-create__issue-create-modal/src/common/utils/use-smart-fields-ml/index.tsx';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { SpotlightTarget } from '@atlassian/jira-spotlight/src/ui/SpotlightTarget.tsx';

const create = defineMessage({
	defaultMessage: 'Create',
	id: 'atlassian-navigation.universal-create.create',
	description: 'Create text for the universal create button',
});

const noopPreload = (): void => {
	// No-op: hover preload is disabled when the on-hover gate is off.
};

const useGicHoverApiPreload = functionWithCondition(
	() => fg('nike_united_gic_on_hover_'),
	(): (() => void) => {
		const { data: projectContext } = useProjectContext();
		const storageVariables = useStorageVariables();
		const { withMachineLearning } = useSmartFieldsMachineLearning();
		return useCallback(() => {
			preloadModalLoadQuery({ projectContext, storageVariables, withMachineLearning });
		}, [projectContext, storageVariables, withMachineLearning]);
	},
	(): (() => void) => noopPreload,
);

function UniversalCreateInner() {
	const [isAifcModalOpen, setIsAifcModalOpen] = useState(false);
	const { formatMessage } = useIntl();
	const triggerAnalytics = useUniversalCreateAnalytics();
	const aiPlannerEnabled = useAiPlannerUniversalCreateEnabled();
	const cloudId = useCloudId();
	const accountId = useAccountId() || '';
	const orgId = useOrgId() || '';
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const sharedPlannerIframe = aiPlannerEnabled ? useAiPlannerIframe() : undefined;
	const warmPlanner = sharedPlannerIframe?.warm;

	useUniversalCreateLoadExperience();
	useUniversalCreateButtonInteractiveExperienceStart();

	const createIssueItem = useCreateIssueItem({});
	const createWithAiItem = useCreateWithAiItem({ onItemClick: setIsAifcModalOpen });
	const createWithPlannerItem = aiPlannerEnabled
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCreateWithPlannerItem()
		: undefined;

	const aiFirstCreateEnabled = useAiFirstCreateEnabled() && !fg('jira_planner_uc_enabled');
	const plannerCreateEnabled = aiPlannerEnabled && createWithPlannerItem !== undefined;
	const shouldShowDefaultCreateButton = useShouldShowDefaultCreateButton();

	const menuSection = useMemo(() => {
		const hasCreateWithAiOptions = aiFirstCreateEnabled || plannerCreateEnabled;

		if (hasCreateWithAiOptions) {
			return {
				items: [
					...(aiFirstCreateEnabled ? [createWithAiItem] : []),
					...(plannerCreateEnabled ? [createWithPlannerItem] : []),
					...(!fg('jira_uc_split_button_enabled') ? [createIssueItem] : []),
				],
			};
		}
		return {
			items: [createIssueItem],
		};
	}, [
		aiFirstCreateEnabled,
		plannerCreateEnabled,
		createIssueItem,
		createWithAiItem,
		createWithPlannerItem,
	]);

	const sections = useMemo(
		(): UniversalCreateMenuSection[] => [
			{
				type: 'appDefinedSection',
				items: menuSection.items,
			},
		],
		[menuSection.items],
	);

	// Primary button needs its own onClick that fires analytics with isPrimary: true
	const primaryActionItem = useMemo(
		() => ({
			...createIssueItem,
			onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
				triggerAnalytics({ isPrimary: true });
				createIssueItem.onClick?.(event);
			},
		}),
		[createIssueItem, triggerAnalytics],
	);

	// Mark interactive experience as complete (items are loaded)
	useUniversalCreateButtonInteractiveExperienceComplete();

	const onDropdownMenuOpen = useCallback(() => {
		// Preload the AI SmartCreationModal on page load
		LazyLoadedSmartCreationModal.preload();
		if (aiPlannerEnabled) {
			warmPlanner?.();
		}
		triggerAnalytics();
	}, [aiPlannerEnabled, triggerAnalytics, warmPlanner]);

	const onDefaultCreateClick = useCallback(() => {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		createIssueItem?.onClick?.({ timeStamp: performance.now() } as React.MouseEvent);
	}, [createIssueItem]);

	const preloadGicApiOnHover = useGicHoverApiPreload();

	const hasPreloadedModernisedModalRef = useRef(false);
	const hasPreloadedGicApiRef = useRef(false);
	const handlePreloadTrigger = useCallback(() => {
		if (!hasPreloadedGicApiRef.current && fg('nike_united_gic_on_hover_')) {
			hasPreloadedGicApiRef.current = true;
			preloadGicApiOnHover();
		}

		if (hasPreloadedModernisedModalRef.current) {
			return;
		}

		if (!fg('jira_preload_modernised_create_modal_on_hover')) {
			return;
		}

		hasPreloadedModernisedModalRef.current = true;
		preloadIssueCreateModernisedModal();
	}, [preloadGicApiOnHover]);

	const conditionalProps = () => {
		if (shouldShowDefaultCreateButton) {
			return { buttonOnly: true as const, onClick: onDefaultCreateClick };
		}

		const menuSections = {
			sections,
		};

		// UC split button variant
		if (fg('jira_uc_split_button_enabled')) {
			return { primaryActionItem, onClick: onDropdownMenuOpen, ...menuSections };
		}

		// UC single button variant
		return { onClick: onDropdownMenuOpen, ...menuSections };
	};
	return (
		<Box
			testId={`${GLOBAL_CREATE_BUTTON_WRAPPER}`}
			onMouseEnter={handlePreloadTrigger}
			onFocus={handlePreloadTrigger}
		>
			{isAifcModalOpen && (
				<Placeholder name="JiraSmartCreationModal" fallback={null}>
					<LazyLoadedSmartCreationModal
						cloudId={cloudId}
						accountId={accountId}
						orgId={orgId}
						onClose={() => setIsAifcModalOpen(false)}
					/>
				</Placeholder>
			)}
			<NavigationCreateIssueButtonWrapperNudgeAsync>
				<CreateButtonSpotlightTarget>
					<UniversalCreateButton
						product="jira"
						testId={GLOBAL_CREATE_BUTTON_NAV4}
						{...conditionalProps()}
					>
						{formatMessage(create)}
					</UniversalCreateButton>
				</CreateButtonSpotlightTarget>
			</NavigationCreateIssueButtonWrapperNudgeAsync>
		</Box>
	);
}

const CreateButtonSpotlightTarget = ({ children }: PropsWithChildren) => {
	if (fg('pol-21470-juno-spotlights-migration')) {
		// TODO: inline it when the FG is cleaned up
		return <SpotlightTarget name="global-create-spotlight">{children}</SpotlightTarget>;
	}

	return <>{children}</>;
};

const UniversalCreateWithPlannerProviders = (): React.JSX.Element => {
	const cloudId = useCloudId();
	const accountId = useAccountId() || '';
	const embedContext = useMemo(() => ({ cloudId, accountId }), [cloudId, accountId]);

	return (
		<AiPlannerIframeProvider embedContext={embedContext}>
			<ModalContextProvider>
				<UniversalCreateInner />
			</ModalContextProvider>
		</AiPlannerIframeProvider>
	);
};

export default function UniversalCreate(): React.JSX.Element {
	return (
		<TopLevelErrorBoundary id={UNIVERSAL_CREATE_BUTTON_ID}>
			{fg('jira_planner_uc_enabled') ? (
				<UniversalCreateWithPlannerProviders />
			) : (
				<UniversalCreateInner />
			)}
		</TopLevelErrorBoundary>
	);
}
